import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import useDeviceType from './useDeviceType';



// Generate a simple S-curve data set for demonstration
// Generalized S-curve formula based on user-provided data
// Formula: drawdown = baselineAmount * (a * (x/y)^3 + b * (x/y)^2 + c * (x/y) + d)
// Coefficients a, b, c, d are fitted to match the provided data sets
function generateSCurveData(baselineAmount, duration) {
    let months = [];
    let n = parseInt(duration, 10);
    if (!n || n < 1) n = 12;
    for (let i = 0; i < n; i++) {
        months.push(`DD${i + 1}`);
    }
    // Known cumulative payment proportions for each duration (from user data)
    const knownCurves = {
        6: [37798, 96905, 165000, 229762, 278869, 300000].map(v => v / 300000),
        8: [39520, 99998, 173493, 252065, 327773, 392675, 438831, 458300].map(v => v / 458300),
        10: [51817, 128823, 223920, 330012, 440000, 546788, 643280, 722377, 776983, 800000].map(v => v / 800000),
        12: [43961, 135208, 263977, 420500, 595013, 777750, 958945, 1128833, 1277648, 1395625, 1472997, 1500000].map(v => v / 1500000)
    };
    const knownDurations = Object.keys(knownCurves).map(Number).sort((a, b) => a - b);

    function sampleCurve(curve, t) {
        if (!Array.isArray(curve) || curve.length === 0) return 0;
        if (curve.length === 1) return curve[0] || 0;
        const x = Math.min(1, Math.max(0, t)) * (curve.length - 1);
        const i0 = Math.floor(x);
        const i1 = Math.ceil(x);
        const v0 = Number(curve[i0] ?? 0);
        const v1 = Number(curve[i1] ?? v0);
        const frac = x - i0;
        const v = v0 + (v1 - v0) * frac;
        return Number.isFinite(v) ? v : 0;
    }

    function getCurve(n) {
        if (knownCurves[n]) return knownCurves[n];
        // Interpolate between nearest known durations for each month
        if (n < knownDurations[0]) {
            // Shorter than shortest known, scale the shortest
            const base = knownCurves[knownDurations[0]];
            return Array.from({ length: n }, (_, i) => sampleCurve(base, i / (n - 1)));
        }
        if (n > knownDurations[knownDurations.length - 1]) {
            // Longer than longest known, scale the longest
            const base = knownCurves[knownDurations[knownDurations.length - 1]];
            return Array.from({ length: n }, (_, i) => sampleCurve(base, i / (n - 1)));
        }
        // Find nearest lower and upper known durations
        let lower = knownDurations[0], upper = knownDurations[knownDurations.length - 1];
        for (let i = 0; i < knownDurations.length - 1; i++) {
            if (n > knownDurations[i] && n < knownDurations[i + 1]) {
                lower = knownDurations[i];
                upper = knownDurations[i + 1];
                break;
            } else if (n === knownDurations[i]) {
                return knownCurves[knownDurations[i]];
            }
        }
        // Interpolate each month between lower and upper
        const lowerCurve = knownCurves[lower];
        const upperCurve = knownCurves[upper];
        return Array.from({ length: n }, (_, i) => {
            const t = i / (n - 1);
            // Map t to index in lower and upper
            const lowerIdx = t * (lowerCurve.length - 1);
            const upperIdx = t * (upperCurve.length - 1);
            // Linear interpolate between lower and upper curves
            const lowerVal = lowerCurve[Math.floor(lowerIdx)] + (lowerCurve[Math.ceil(lowerIdx)] - lowerCurve[Math.floor(lowerIdx)]) * (lowerIdx - Math.floor(lowerIdx));
            const upperVal = upperCurve[Math.floor(upperIdx)] + (upperCurve[Math.ceil(upperIdx)] - upperCurve[Math.floor(upperIdx)]) * (upperIdx - Math.floor(upperIdx));
            const frac = (n - lower) / (upper - lower);
            return lowerVal + (upperVal - lowerVal) * frac;
        });
    }
    const curve = getCurve(n);
    return months.map((m, i) => ({
        month: m,
        drawdown: Math.round(baselineAmount * curve[i])
    }));
}


export default function SCurveChart({ baselineAmount, baselineSource, duration, storageKeyPrefix, prefillActuals }) {
    const { isPhone } = useDeviceType();
    const base = Number(baselineAmount);
    const source = String(baselineSource || '').trim().toLowerCase();
    const prefix = storageKeyPrefix ? String(storageKeyPrefix) : 'baseline';
    // Use a unique key per project/baseline/duration to avoid clobbering saved actuals.
    const storageKey = `actuals_${prefix}_${Math.round(base || 0)}_${duration}`;
    const [actuals, setActuals] = useState(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) return JSON.parse(saved);
        } catch { }
        return [];
    });

    // Reload actuals if the storage key changes (e.g., different project/baseline/duration).
    useEffect(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed)) {
                    setActuals(parsed);
                    return;
                }
            }
        } catch { }
        setActuals([]);
    }, [storageKey]);

    // Always define data, even if baseline is invalid, to keep hooks order
    const data = (!base || isNaN(base) || base <= 0)
        ? []
        : generateSCurveData(base, duration);


    // Ensure actuals array matches data length
    useEffect(() => {
        if (actuals.length !== data.length) {
            setActuals(a => {
                const copy = [...a];
                copy.length = data.length;
                for (let i = 0; i < data.length; i++) {
                    if (typeof copy[i] === 'undefined') copy[i] = '';
                }
                return copy;
            });
        }
        // eslint-disable-next-line
    }, [data.length]);

    // Prefill actuals (e.g., from Interim Reports). When provided, these become the source of truth
    // for the matching month(s), overriding any previously saved/manual values.
    useEffect(() => {
        if (!Array.isArray(prefillActuals) || prefillActuals.length === 0) return;
        if (!data.length) return;

        setActuals((prev) => {
            const next = [...prev];
            let changed = false;

            for (let i = 0; i < data.length; i++) {
                const raw = prefillActuals[i];
                if (raw === null || raw === undefined || raw === '') continue;
                const num = Number(raw);
                if (!Number.isFinite(num) || num < 0) continue;

                const cur = String(next[i] ?? '').trim();
                const nextVal = String(num);
                if (cur !== nextVal) {
                    next[i] = nextVal;
                    changed = true;
                }
            }

            return changed ? next : prev;
        });
    }, [prefillActuals, data.length]);

    // Save actuals to localStorage whenever they change
    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(actuals));
        } catch { }
    }, [actuals, storageKey]);

    const formatPounds = useMemo(() => {
        function toNumber(v) {
            const n = typeof v === 'string' ? Number(String(v).replace(/,/g, '')) : Number(v);
            return Number.isFinite(n) ? n : 0;
        }

        function compact(n) {
            const sign = n < 0 ? '-' : '';
            const abs = Math.abs(n);
            if (abs >= 1_000_000) return `${sign}£${(abs / 1_000_000).toFixed(2)}M`;
            if (abs >= 1_000) return `${sign}£${Math.round(abs / 1_000)}K`;
            return `${sign}£${Math.round(abs)}`;
        }

        function full(n) {
            const num = toNumber(n);
            const sign = num < 0 ? '-' : '';
            const abs = Math.abs(num);
            return `${sign}£${abs.toLocaleString()}`;
        }

        return (v) => {
            const n = toNumber(v);
            return isPhone ? compact(n) : full(n);
        };
    }, [isPhone]);

    const formatSignedPounds = useMemo(() => {
        return (v) => {
            const n = Number(v);
            if (!Number.isFinite(n)) return '-';
            const rounded = Math.round(n);
            const absFormatted = formatPounds(Math.abs(rounded));
            if (absFormatted === '-') return '-';
            if (rounded === 0) return absFormatted;
            return `${rounded > 0 ? '+' : '-'}${absFormatted}`;
        };
    }, [formatPounds]);

    const labels = useMemo(() => {
        return {
            tableTitle: isPhone ? 'DD Table' : 'Drawdown Table',
            month: isPhone ? 'DD' : 'Month',
            anticipatedCum: isPhone ? 'Est. Cum.' : 'Anticipated Cumulative Drawdown',
            anticipatedInd: isPhone ? 'Est.' : 'Anticipated Individual Drawdown',
            actual: isPhone ? 'Act.' : 'Actual Drawdown',
            actualCum: isPhone ? 'Act. Cum.' : 'Actual Cumulative Drawdown',
            delta: isPhone ? 'Δ' : 'Delta',
            legendAnticipatedCum: isPhone ? 'Est. Cum. DD' : 'Cumulative Drawdown (Anticipated)',
            legendActual: isPhone ? 'Act. DD' : 'Actual Drawdown',
            legendActualCum: isPhone ? 'Act. Cum. DD' : 'Actual Cumulative Drawdown'
        };
    }, [isPhone]);

    // Early return for invalid baseline (after hooks)
    if (!base || isNaN(base) || base <= 0) {
        return <div style={{ color: '#b91c1c', padding: '1rem', background: '#fef2f2', borderRadius: 8, margin: '1rem 0' }}>
            Please provide Construction Cost (and Contingency) in the Project Costs table to view the S-Curve diagram.
        </div>;
    }


    // Calculate actual cumulative drawdown, only accumulating where actual drawdown > 0
    const actualCumulative = actuals.reduce((arr, val, idx) => {
        const prev = idx === 0 ? 0 : arr[idx - 1];
        const num = parseFloat(val) > 0 ? parseFloat(val) : 0;
        arr.push(prev + num);
        return arr;
    }, []);

    const hasAnyActual = actuals.some((v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) && n > 0;
    });

    // Find the last month index that has actual drawdown data
    const lastActualIdx = (() => {
        let last = -1;
        actuals.forEach((v, idx) => {
            const n = parseFloat(v);
            if (Number.isFinite(n) && n > 0) last = idx;
        });
        return last;
    })();

    // Prepare chart data with anticipated, actual, and cumulative actuals
    const chartData = data.map((row, idx) => ({
        month: row.month,
        drawdown: row.drawdown,
        actualDrawdown: parseFloat(actuals[idx]) > 0 ? parseFloat(actuals[idx]) : null,
        // Only show cumulative line up to the last month with actual data
        actualCumulative: hasAnyActual && idx <= lastActualIdx ? (actualCumulative[idx] ?? 0) : null
    }));

    // Calculate anticipated individual drawdown (difference between months)
    const anticipatedIndividual = data.map((row, idx) => idx === 0 ? row.drawdown : row.drawdown - data[idx - 1].drawdown);

    // Calculate Delta: Actual Cumulative Drawdown - Anticipated Cumulative Drawdown
    const delta = data.map((row, idx) => (actualCumulative[idx] ?? 0) - row.drawdown);

    return (
        <>
            {source === 'developer' && (
                <div style={{ marginBottom: 10, color: '#6b7280', fontSize: 12 }}>
                    Baseline uses developer costs (surveyor assessment not provided yet).
                </div>
            )}
            <div style={{ width: '100%', height: 320, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                    <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={v => formatPounds(v)} />
                        <Tooltip formatter={v => formatPounds(v)} />
                        <Legend />
                        <Line type="monotone" dataKey="drawdown" stroke="#2563eb" strokeWidth={3} dot={{ r: 4 }} name={labels.legendAnticipatedCum} />
                        <Bar dataKey="actualDrawdown" fill="#f59e42" name={labels.legendActual} barSize={isPhone ? 18 : 24} />
                        <Line type="monotone" dataKey="actualCumulative" stroke="#e11d48" strokeWidth={3} dot={{ r: 4 }} name={labels.legendActualCum} connectNulls isAnimationActive={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            {/* Drawdown Table */}
            <div style={{ marginTop: 32 }}>
                <h4>{labels.tableTitle}</h4>
                <div className="reports-scroll-x">
                    <table
                        style={{
                            width: '100%',
                            minWidth: isPhone ? 0 : 760,
                            tableLayout: isPhone ? 'fixed' : 'fixed',
                            borderCollapse: 'collapse',
                            background: '#fff',
                            fontSize: isPhone ? 12 : 15
                        }}
                    >
                        {!isPhone && (
                            <colgroup>
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '20%' }} />
                                <col style={{ width: '20%' }} />
                                <col style={{ width: '17%' }} />
                                <col style={{ width: '18%' }} />
                                <col style={{ width: '13%' }} />
                            </colgroup>
                        )}
                        <thead>
                            <tr style={{ background: '#f3f4f6' }}>
                                {isPhone ? (
                                    <>
                                        <th style={{ padding: 5, border: '1px solid #e5e7eb', width: '18%', textAlign: 'center', whiteSpace: 'nowrap' }}>{labels.month}</th>
                                        <th style={{ padding: 5, border: '1px solid #e5e7eb', width: '41%', textAlign: 'right', whiteSpace: 'nowrap' }}>{labels.anticipatedInd}</th>
                                        <th style={{ padding: 5, border: '1px solid #e5e7eb', width: '41%', textAlign: 'right', whiteSpace: 'nowrap' }}>{labels.actual}</th>
                                    </>
                                ) : (
                                    <>
                                        <th style={{ padding: 8, border: '1px solid #e5e7eb', whiteSpace: 'normal', lineHeight: 1.2 }}>{labels.month}</th>
                                        <th style={{ padding: 8, border: '1px solid #e5e7eb', whiteSpace: 'normal', lineHeight: 1.2 }}>{labels.anticipatedCum}</th>
                                        <th style={{ padding: 8, border: '1px solid #e5e7eb', whiteSpace: 'normal', lineHeight: 1.2 }}>{labels.anticipatedInd}</th>
                                        <th style={{ padding: 8, border: '1px solid #e5e7eb', whiteSpace: 'normal', lineHeight: 1.2 }}>{labels.actual}</th>
                                        <th style={{ padding: 8, border: '1px solid #e5e7eb', whiteSpace: 'normal', lineHeight: 1.2 }}>{labels.actualCum}</th>
                                        <th style={{ padding: 8, border: '1px solid #e5e7eb', whiteSpace: 'normal', lineHeight: 1.2 }}>{labels.delta}</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, idx) => (
                                <tr key={row.month}>
                                    {isPhone ? (
                                        <>
                                            <td style={{ padding: 5, border: '1px solid #e5e7eb', textAlign: 'center', whiteSpace: 'nowrap' }}>{row.month}</td>
                                            <td style={{ padding: 5, border: '1px solid #e5e7eb', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{formatPounds(anticipatedIndividual[idx])}</td>
                                            <td style={{ padding: 5, border: '1px solid #e5e7eb', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                                <span title={Array.isArray(prefillActuals) ? 'Auto-linked from Interim Report drawdown (read-only)' : 'Read-only'}>
                                                    {parseFloat(actuals[idx]) > 0 ? formatPounds(actuals[idx]) : '—'}
                                                </span>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td style={{ padding: 8, border: '1px solid #e5e7eb', textAlign: 'center', whiteSpace: 'nowrap' }}>{row.month}</td>
                                            <td style={{ padding: 8, border: '1px solid #e5e7eb', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{formatPounds(row.drawdown)}</td>
                                            <td style={{ padding: 8, border: '1px solid #e5e7eb', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{formatPounds(anticipatedIndividual[idx])}</td>
                                            <td style={{ padding: 8, border: '1px solid #e5e7eb', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                                <span title={Array.isArray(prefillActuals) ? 'Auto-linked from Interim Report drawdown (read-only)' : 'Read-only'}>
                                                    {parseFloat(actuals[idx]) > 0 ? formatPounds(Math.round(Number(actuals[idx] || 0))) : '—'}
                                                </span>
                                            </td>
                                            <td style={{ padding: 8, border: '1px solid #e5e7eb', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{formatPounds(actualCumulative[idx] ?? 0)}</td>
                                            <td style={{ padding: 8, border: '1px solid #e5e7eb', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: delta[idx] === 0 ? '#111' : (delta[idx] > 0 ? '#059669' : '#dc2626') }}>
                                                {parseFloat(actuals[idx]) > 0
                                                    ? formatSignedPounds(delta[idx])
                                                    : ''}
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}
